import { useMergeRefs } from "@floating-ui/react"
import classNames from "classnames"
import type {
  Ref,
  ComponentPropsWithoutRef,
  KeyboardEventHandler,
  MouseEventHandler,
  FocusEventHandler,
} from "react"
import {
  forwardRef,
  useCallback,
  useRef,
} from "react"
import { Flipped } from "react-flip-toolkit"

import styles from "./Toast.module.scss"
import { useToastContext } from "./useToastContext"

enum StylePreset {
  success = "toastNotificationSuccess",
  error = "toastNotificationError",
}

type Props = ComponentPropsWithoutRef<"button">

/*
 * thie component renders the visual toast notification
 * and wraps it in a container that can be animated by the Toaster
 */
export const ToastNotification = forwardRef<HTMLButtonElement, Props>(
  function ToastNotification(props: Props, propRef: Ref<HTMLButtonElement>) {
    const notificationRef = useRef<HTMLButtonElement>(null)

    // when this is true, the mouse leave handler blurs the notification
    const mouseEnterRef = useRef<boolean>(false)

    const ref = useMergeRefs([
      propRef,
      notificationRef,
    ])

    const {
      stylePreset,
      toastId,
      setOpen,
      setFocused,
    } = useToastContext()

    const className = classNames(
      styles[StylePreset[stylePreset]],
      props.className,
    )

    // dismiss the notification when the escape key is pressed
    const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (e.key === "Escape") {
          setOpen(false)
        }

        props.onKeyDown?.(e)
      },
      [
        setOpen,
        props.onKeyDown,
      ],
    )

    // focus the notification when it is clicked
    const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (notificationRef.current) {
          mouseEnterRef.current = false
          notificationRef.current.focus()
        }

        props.onClick?.(e)
      },
      [
        notificationRef,
        props.onClick,
      ],
    )

    // focus the notification when the mouse hovers over it
    const onMouseEnter: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (notificationRef.current) {
          mouseEnterRef.current = true
          notificationRef.current.focus()
        }

        props.onMouseEnter?.(e)
      },
      [props.onMouseEnter],
    )

    // unfocus the notification when the mouse leaves it
    const onMouseLeave: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (notificationRef.current && mouseEnterRef.current) {
          mouseEnterRef.current = false
          notificationRef.current.blur()
        }

        props.onMouseLeave?.(e)
      },
      [props.onMouseLeave],
    )

    // prevent the notification from exiting while it is being focused
    const onFocus: FocusEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        setFocused(true)
        props.onFocus?.(e)
      },
      [
        props.onFocus,
        setFocused,
      ],
    )

    // resume normal notification exit behavior
    const onBlur: FocusEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        setFocused(false)
        props.onBlur?.(e)
      },
      [
        props.onBlur,
        setFocused,
      ],
    )

    return (
      <Flipped flipId={toastId}>
        <button
          {...props}
          ref={ref}
          className={className}
          onKeyDown={onKeyDown}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={onClick}
        >
          {props.children}
        </button>
      </Flipped>
    )
  },
)
